import { ErrorReportContext } from './error-report-context';

/** Minimal storage surface (a subset of `Storage`) used to persist dedup state. */
export type DedupStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** Tuning for crash-loop deduplication and rate limiting. */
export interface DedupOptions {
  /** Sliding window length, in milliseconds. */
  windowMs: number;
  /** Maximum number of reports allowed within the window. */
  maxReports: number;
}

export const DEFAULT_DEDUP_OPTIONS: DedupOptions = {
  windowMs: 60_000,
  maxReports: Number.POSITIVE_INFINITY,
};

const STORAGE_KEY = 'imagise.error-reporting.dedup';

interface DedupState {
  /** fingerprint -> last-seen epoch ms. */
  seen: Record<string, number>;
  /** epoch ms of recent sends, for rate limiting. */
  sends: number[];
}

/**
 * Computes a stable fingerprint for an error so that repeated occurrences of the
 * same crash collapse to one identity. Built from the error type, message,
 * path-only URL (query/hash stripped) and the first stack line.
 */
export function fingerprintErrorContext(
  context: Pick<ErrorReportContext, 'type' | 'message' | 'pageUrl' | 'errors'>
): string {
  const firstStackLine = (context.errors[0]?.Details ?? '').split('\n')[0] ?? '';
  const key = [
    context.type,
    context.message,
    stripUrlNoise(context.pageUrl),
    firstStackLine,
  ].join('|');

  return hashString(key);
}

/**
 * Pure crash-loop guard. Decides whether a given fingerprint should be
 * suppressed because the same crash was just reported, or because too many
 * reports were sent within the window. State is persisted through an injected
 * {@link DedupStorage} so suppression survives a reload (crash-loop reloads).
 */
export class ErrorReportDeduplicator {
  readonly #storage: DedupStorage | null;
  readonly #options: DedupOptions;
  readonly #now: () => number;
  #memory: DedupState = { seen: {}, sends: [] };

  constructor(
    storage: DedupStorage | null,
    options: DedupOptions = DEFAULT_DEDUP_OPTIONS,
    now: () => number = () => Date.now()
  ) {
    this.#storage = storage;
    this.#options = options;
    this.#now = now;
  }

  /**
   * Returns `true` if the report for `fingerprint` should be dropped. When it
   * returns `false`, the occurrence is recorded so subsequent duplicates within
   * the window are suppressed.
   */
  shouldSuppress(fingerprint: string): boolean {
    const now = this.#now();
    const windowStart = now - this.#options.windowMs;
    const state = this.#read();

    state.sends = state.sends.filter(ts => ts >= windowStart);
    for (const key of Object.keys(state.seen)) {
      if (state.seen[key] < windowStart) {
        delete state.seen[key];
      }
    }

    const isDuplicate = state.seen[fingerprint] !== undefined;
    const isRateLimited = state.sends.length >= this.#options.maxReports;

    if (isDuplicate || isRateLimited) {
      // Refresh the duplicate timestamp so an ongoing loop keeps being suppressed.
      state.seen[fingerprint] = now;
      this.#write(state);
      return true;
    }

    state.seen[fingerprint] = now;
    state.sends.push(now);
    this.#write(state);
    return false;
  }

  #read(): DedupState {
    if (!this.#storage) {
      return this.#memory;
    }

    try {
      const raw = this.#storage.getItem(STORAGE_KEY);
      if (!raw) {
        return this.#memory;
      }
      const parsed = JSON.parse(raw) as Partial<DedupState>;
      if (!parsed.seen || typeof parsed.seen !== 'object' || Array.isArray(parsed.seen)) {
        return this.#memory;
      }
      return {
        seen: Object.fromEntries(Object.entries(parsed.seen).filter(([, value]) => Number.isFinite(value))),
        sends: Array.isArray(parsed.sends) ? parsed.sends.filter(value => Number.isFinite(value)) : [],
      };
    } catch {
      return this.#memory;
    }
  }

  #write(state: DedupState): void {
    this.#memory = state;
    if (!this.#storage) {
      return;
    }

    try {
      this.#storage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage unavailable / quota exceeded — degrade silently.
    }
  }
}

/** Strips query string and hash so URL params don't fragment the fingerprint. */
function stripUrlNoise(url: string): string {
  return url.split('#')[0].split('?')[0];
}

/** Deterministic, non-cryptographic string hash (cyrb53) rendered as base36. */
/* eslint-disable @typescript-eslint/no-magic-numbers -- cyrb53 algorithm constants */
function hashString(input: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;

  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  const hash = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return hash.toString(36);
}
/* eslint-enable @typescript-eslint/no-magic-numbers */
